import type { LucideIcon } from "lucide-react";
import { cn } from "../../lib/utils";
import { domainClasses, type Domain } from "./domains";

export type IconTileSize = "xxs" | "xs" | "s" | "m" | "l" | "xl";

const sizes: Record<
  IconTileSize,
  { tile: string; icon: string; stroke: number }
> = {
  /** Inline well: a nav item, a key fact, a form-section title (28 px). */
  xxs: { tile: "h-7 w-7 rounded-lg", icon: "h-4 w-4", stroke: 1.75 },
  xs: { tile: "h-11 w-11 rounded-xl", icon: "h-[22px] w-[22px]", stroke: 1.75 },
  s: {
    tile: "h-[52px] w-[52px] rounded-[15px]",
    icon: "h-[26px] w-[26px]",
    stroke: 1.75,
  },
  m: {
    tile: "h-[76px] w-[76px] rounded-[22px]",
    icon: "h-[38px] w-[38px]",
    stroke: 1.75,
  },
  l: {
    tile: "h-[88px] w-[88px] rounded-[26px]",
    icon: "h-11 w-11",
    stroke: 1.75,
  },
  xl: {
    tile: "h-[140px] w-[140px] rounded-[36px]",
    icon: "h-[70px] w-[70px]",
    stroke: 1.5,
  },
};

export interface IconTileProps {
  icon: LucideIcon;
  domain: Domain;
  size?: IconTileSize;
  /** `tint` is the dashboard icon well: tinted plate, ink icon. */
  variant?: "solid" | "tint";
  /** Layout only (margin, grid placement). */
  className?: string;
}

/**
 * The identity mark of a card or section. One per card; never decorates a
 * field. `xs` (44 px) is the section/card identity well on the dashboard;
 * `xxs` (28 px) is the compact inline well.
 */
export function IconTile({
  icon: Icon,
  domain,
  size = "m",
  variant = "solid",
  className,
}: IconTileProps) {
  const d = domainClasses[domain];
  const s = sizes[size];
  return (
    <span
      aria-hidden="true"
      data-domain={domain}
      className={cn(
        "inline-grid shrink-0 place-items-center",
        s.tile,
        variant === "solid"
          ? cn(
              d.solid,
              "text-caire-on-solid shadow-[0_10px_24px_-10px]",
              d.tileShadow,
            )
          : cn(d.tint, d.ink),
        className,
      )}
    >
      <Icon className={s.icon} strokeWidth={s.stroke} />
    </span>
  );
}
